import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StudentExportData {
  id: number;
  user_id: number;
  name: string;
  father_name: string;
  mother_name: string;
  address: string;
  father_no: string;
  mother_no: string;
  gender: string;
  profile_picture_url: string;
  admission_no: string;
  class_name: string;
  section: string | null;
  roll_no: number;
}

interface ExportStudentsZipProps {
  students: StudentExportData[];
  label?: string;
  disabled?: boolean;
}

type BarcodeType = 'linear' | 'qr';

// ─── Linear barcode renderer (Code128 — pure canvas) ─────────────────────────

const CODE128_MAP: Record<string, number[]> = {
  ' ': [2,1,2,2,2,2], '!': [2,2,2,1,2,2], '"': [2,2,2,2,2,1],
  '#': [1,2,1,2,2,3], '$': [1,2,1,3,2,2], '%': [1,3,1,2,2,2],
  '&': [1,2,2,2,1,3], "'": [1,2,2,3,1,2], '(': [1,3,2,2,1,2],
  ')': [2,2,1,2,1,3], '*': [2,2,1,3,1,2], '+': [2,3,1,2,1,2],
  ',': [1,1,2,2,3,2], '-': [1,2,2,1,3,2], '.': [1,2,2,2,3,1],
  '/': [1,1,3,2,2,2], '0': [1,2,3,1,2,2], '1': [1,2,3,2,2,1],
  '2': [2,2,3,2,1,1], '3': [2,2,1,1,3,2], '4': [2,2,1,2,3,1],
  '5': [2,1,3,2,1,2], '6': [2,2,3,1,1,2], '7': [3,1,2,1,3,1],
  '8': [3,1,1,2,2,2], '9': [3,2,1,1,2,2], ':': [3,2,1,2,2,1],
  ';': [3,1,2,2,1,2], '<': [3,2,2,1,1,2], '=': [3,2,2,2,1,1],
  '>': [2,1,2,1,2,3], '?': [2,1,2,3,2,1], '@': [2,3,2,1,2,1],
  'A': [1,1,1,3,2,3], 'B': [1,3,1,1,2,3], 'C': [1,3,1,3,2,1],
  'D': [1,1,2,3,1,3], 'E': [1,3,2,1,1,3], 'F': [1,3,2,3,1,1],
  'G': [2,1,1,3,1,3], 'H': [2,3,1,1,1,3], 'I': [2,3,1,3,1,1],
  'J': [1,1,3,1,2,3], 'K': [1,1,3,3,2,1], 'L': [1,3,3,1,2,1],
  'M': [1,1,2,1,3,3], 'N': [1,1,2,3,3,1], 'O': [1,3,2,1,3,1],
  'P': [3,1,1,1,2,3], 'Q': [3,1,1,3,2,1], 'R': [3,3,1,1,2,1],
  'S': [3,1,2,1,1,3], 'T': [3,1,2,3,1,1], 'U': [3,3,2,1,1,1],
  'V': [3,1,4,1,1,1], 'W': [2,2,1,4,1,1], 'X': [4,3,1,1,1,1],
  'Y': [1,1,1,2,2,4], 'Z': [1,1,1,4,2,2],
};

function renderLinearBarcode(canvas: HTMLCanvasElement, data: string, scale = 3): void {
  const START_B = [2,1,1,4,1,2];
  const STOP    = [2,3,3,1,1,1,2];
  const chars   = data.toUpperCase().split('').filter((c) => CODE128_MAP[c]);
  const bars: number[][] = [START_B];
  let checksum = 104;
  chars.forEach((c, i) => {
    const idx = c.charCodeAt(0) - 32;
    checksum += idx * (i + 1);
    bars.push(CODE128_MAP[c] || CODE128_MAP[' ']);
  });
  const checkVal  = checksum % 103;
  const checkChar = String.fromCharCode(checkVal + 32);
  bars.push(CODE128_MAP[checkChar] || CODE128_MAP[' ']);
  bars.push(STOP);

  const barWidth  = scale;
  const height    = 44;
  const paddingX  = 7;
  const paddingY  = 7;
  const totalBars = bars.reduce((sum, b) => sum + b.reduce((a, v) => a + v, 0), 0);
  const width     = totalBars * barWidth + paddingX * 2;

  canvas.width  = width;
  canvas.height = height + paddingY * 2;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let x    = paddingX;
  let dark = true;
  bars.forEach((bar) => {
    bar.forEach((w) => {
      if (dark) {
        ctx.fillStyle = '#000';
        ctx.fillRect(x, paddingY, w * barWidth, height);
      }
      x += w * barWidth;
      dark = !dark;
    });
  });
}

// ─── Blob helpers ─────────────────────────────────────────────────────────────

function linearBarcodePngBlob(data: string): Promise<Blob> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    renderLinearBarcode(canvas, data, 3);
    canvas.toBlob((blob) => resolve(blob!), 'image/png');
  });
}

async function qrCodePngBlob(data: string): Promise<Blob> {
  let QRCode: any;
  try { QRCode = (await import('qrcode')).default; }
  catch { QRCode = (window as any).QRCode; }

  if (!QRCode) throw new Error('qrcode package not available. Run: npm install qrcode');

  const canvas = document.createElement('canvas');
  await QRCode.toCanvas(canvas, data, {
    errorCorrectionLevel: 'M',
    margin: 2,
    width: 300,
    color: { dark: '#000000', light: '#ffffff' },
  });

  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('QR canvas toBlob failed')), 'image/png')
  );
}

async function fetchImageAsBlob(url: string): Promise<Blob | null> {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

function generateCSV(students: StudentExportData[]): string {
  const esc = (v: string | number | null | undefined) => {
    const s = String(v ?? '').replace(/"/g, '""');
    return `"${s}"`;
  };
  const headers = [
    'S.No.', 'Student ID', 'Name', 'Father Name', 'Mother Name',
    'Address', 'Contact No. (Father)', 'Contact No. (Mother)',
    'Gender', 'Class', 'Profile Picture File', 'Barcode File',
  ];
  const rows = students.map((s, i) => [
    i + 1, esc(s.id), esc(s.name), esc(s.father_name), esc(s.mother_name),
    esc(s.address), esc(s.father_no), esc(s.mother_no), esc(s.gender),
    esc(s.class_name),
    esc(`${s.id}.jpg`),
    esc(`000${s.id}.png`),
  ]);
  return [headers.join(','), ...rows.map((r) => r.join(','))].join('\r\n');
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 16, paddingBottom: 12,
    borderBottom: '1px solid rgba(229,231,235,0.6)',
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 32, height: 32, borderRadius: 8,
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
      color: 'white', flexShrink: 0,
    }}>
      <i className={icon} style={{ fontSize: 15 }} />
    </div>
    <span style={{
      color: '#1e3a8a', fontWeight: 700, fontSize: 14,
      letterSpacing: '0.02em', textTransform: 'uppercase',
    }}>
      {title}
    </span>
  </div>
);

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{
    background: 'rgba(255,255,255,0.8)', borderRadius: 16, padding: 20,
    border: '1px solid rgba(59,130,246,0.1)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    ...style,
  }}>
    {children}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const ExportStudentsZip: React.FC<ExportStudentsZipProps> = ({
  students,
  label = 'Export ZIP',
  disabled = false,
}) => {
  const toast = useRef<Toast>(null);
  const [visible, setVisible]                 = useState(false);
  const [isClosing, setIsClosing]             = useState(false);
  const [skipNoPicture, setSkipNoPicture]     = useState(false);
  const [showIndividual, setShowIndividual]   = useState(true);
  const [manuallySkipped, setManuallySkipped] = useState<Set<number>>(new Set());
  const [barcodePrefix, setBarcodePrefix]     = useState('');
  const [barcodeType, setBarcodeType]         = useState<BarcodeType>('linear');
  const [progress, setProgress]               = useState(0);
  const [progressLabel, setProgressLabel]     = useState('');
  const [exporting, setExporting]             = useState(false);
  const [done, setDone]                       = useState(false);
  const abortRef                              = useRef(false);

  useEffect(() => {
    if (isClosing) {
      document.querySelector('.p-dialog')?.classList.add('dialog-closing');
      document.querySelector('.p-dialog-mask')?.classList.add('mask-closing');
    }
  }, [isClosing]);

  const noPictureIds = useMemo(
    () => new Set(students.filter((s) => !s.profile_picture_url?.trim()).map((s) => s.id)),
    [students],
  );

  const studentsToExport = useMemo(() => {
    return students.filter((s) => {
      if (skipNoPicture && noPictureIds.has(s.id)) return false;
      if (manuallySkipped.has(s.id)) return false;
      return true;
    });
  }, [students, skipNoPicture, noPictureIds, manuallySkipped]);

  const skippedCount = students.length - studentsToExport.length;

  const reset = () => { setProgress(0); setProgressLabel(''); setDone(false); abortRef.current = false; };

  const handleOpen = () => {
    reset();
    setSkipNoPicture(false); setShowIndividual(true);
    setManuallySkipped(new Set()); setBarcodePrefix('');
    setBarcodeType('linear'); setVisible(true);
  };

  const handleClose = () => {
    if (exporting) return;
    abortRef.current = true;
    setIsClosing(true);
    setTimeout(() => { setVisible(false); setExporting(false); reset(); setIsClosing(false); }, 400);
  };

  const toggleManualSkip = (id: number) => {
    setManuallySkipped((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const skipAllNoPicture = () => {
    setManuallySkipped((prev) => {
      const next = new Set(prev);
      noPictureIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearAllSkips = () => { setManuallySkipped(new Set()); setSkipNoPicture(false); };

  // ─── Export ───────────────────────────────────────────────────────────────

  const runExport = async () => {
    if (!studentsToExport.length) return;
    setExporting(true); setDone(false); abortRef.current = false;

    let JSZip: any;
    try { JSZip = (await import('jszip')).default; }
    catch { JSZip = (window as any).JSZip; }

    if (!JSZip) {
      toast.current?.show({
        severity: 'error', summary: 'Missing dependency',
        detail: 'JSZip is not available. Please install it: npm install jszip', life: 6000,
      });
      setExporting(false); return;
    }

    const zip           = new JSZip();
    const profileFolder = zip.folder('profile_pictures')!;
    const barcodeFolder = zip.folder('barcodes')!;
    const total         = studentsToExport.length;

    setProgressLabel('Generating student data CSV…'); setProgress(2);
    zip.file('students.csv', generateCSV(studentsToExport));

    for (let i = 0; i < total; i++) {
      if (abortRef.current) break;
      const s           = studentsToExport[i];
      const pct         = Math.round(((i + 1) / total) * 90) + 5;
      const barcodeBase = `000${s.id}`;
      setProgress(pct);
      setProgressLabel(`Processing ${i + 1} / ${total}: ${s.name}`);

      const imgBlob = await fetchImageAsBlob(s.profile_picture_url);
      const ext     = s.profile_picture_url?.includes('.png') ? 'png' : 'jpg';
      if (imgBlob) profileFolder.file(`${s.id}.${ext}`, imgBlob);
      else         profileFolder.file(`${s.id}_NO_IMAGE.txt`, 'Profile picture not available.');

      const barcodeData = barcodePrefix ? `${barcodePrefix}${s.id}` : String(s.id);
      try {
        const barcodeBlob = barcodeType === 'qr'
          ? await qrCodePngBlob(barcodeData)
          : await linearBarcodePngBlob(barcodeData);
        barcodeFolder.file(`${barcodeBase}.png`, barcodeBlob);
      } catch (err: any) {
        if (barcodeType === 'qr' && err?.message?.includes('qrcode')) {
          toast.current?.show({
            severity: 'error', summary: 'Missing dependency',
            detail: 'QR code requires the qrcode package. Run: npm install qrcode', life: 8000,
          });
          abortRef.current = true; break;
        }
        barcodeFolder.file(`${barcodeBase}_ERROR.txt`, `Failed to generate barcode: ${err?.message}`);
      }
    }

    if (abortRef.current) { setExporting(false); return; }

    setProgressLabel('Compressing ZIP…'); setProgress(97);
    const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });

    setProgress(100); setProgressLabel('Done!');

    const url  = URL.createObjectURL(zipBlob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `students_export_${new Date().toISOString().slice(0, 10)}.zip`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);

    setDone(true); setExporting(false);
    toast.current?.show({
      severity: 'success', summary: 'Export Complete',
      detail: `ZIP file with ${total} student${total !== 1 ? 's' : ''} downloaded.`, life: 5000,
    });
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div style={{
        width: 54, height: 54, borderRadius: 14,
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: 24, fontWeight: 700,
        boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
        flexShrink: 0, border: '3px solid white',
      }}>
        <i className="pi pi-file-export" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: 4 }}>
          Export Students ZIP
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {students.length} student{students.length !== 1 ? 's' : ''} · CSV + Photos + Barcodes
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Button
        icon="pi pi-download"
        label={label}
        onClick={handleOpen}
        disabled={disabled || students.length === 0}
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          border: 'none', borderRadius: 10, padding: '9px 18px',
          fontSize: 13, fontWeight: 600, color: 'white', gap: 6,
          boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
        }}
        tooltip={students.length === 0 ? 'Load students first to export' : `Export ${students.length} students as ZIP`}
        tooltipOptions={{ position: 'top' }}
      />

      <Dialog
        visible={visible && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        closable={!exporting}
        style={{ width: '90vw', maxWidth: 560, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)', overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px', borderTop: 'none',
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal dismissableMask={!exporting} draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: maskFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn { from { opacity:0; } to { opacity:1; } }
          @keyframes maskFadeOut { from { opacity:1; } to { opacity:0; } }
          .p-dialog { animation: dialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes dialogOpen { from { opacity:0; transform:scale(0.92) translateY(-20px); } to { opacity:1; transform:scale(1) translateY(0); } }
          @keyframes dialogClose { from { opacity:1; transform:scale(1) translateY(0); } to { opacity:0; transform:scale(0.88) translateY(25px); } }
          .student-row:hover { background: rgba(59,130,246,0.04) !important; }
          .ez-cancel-btn:hover { border-color:#3b82f6 !important; color:#1e40af !important; }
          .ez-export-btn:hover:not(:disabled) { box-shadow: 0 6px 20px rgba(59,130,246,0.45) !important; transform: translateY(-1px); }
          .bc-type-btn { transition: all 0.18s ease; }
          .bc-type-btn:hover { transform: translateY(-1px); }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {!exporting && !done && (
            <>
              {/* ── Export Options card ── */}
              <Card>
                <SectionHeader icon="pi pi-sliders-h" title="Export Options" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                  {/* Skip no-picture */}
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    padding: '10px 14px', borderRadius: 10,
                    background: skipNoPicture ? 'rgba(59,130,246,0.06)' : 'rgba(248,250,252,0.9)',
                    border: `1px solid ${skipNoPicture ? 'rgba(59,130,246,0.25)' : '#e5e7eb'}`,
                    transition: 'all 0.2s ease',
                  }}>
                    <Checkbox inputId="skip-no-pic" checked={skipNoPicture}
                      onChange={(e) => setSkipNoPicture(e.checked ?? false)} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                        Skip students without profile picture
                      </div>
                      {noPictureIds.size > 0 && (
                        <div style={{ fontSize: 11, color: '#d97706', marginTop: 2, fontWeight: 500 }}>
                          {noPictureIds.size} student{noPictureIds.size !== 1 ? 's' : ''} affected
                        </div>
                      )}
                    </div>
                  </label>

                  {/* Show individual controls */}
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    padding: '10px 14px', borderRadius: 10,
                    background: showIndividual ? 'rgba(59,130,246,0.06)' : 'rgba(248,250,252,0.9)',
                    border: `1px solid ${showIndividual ? 'rgba(59,130,246,0.25)' : '#e5e7eb'}`,
                    transition: 'all 0.2s ease',
                  }}>
                    <Checkbox inputId="show-individual" checked={showIndividual}
                      onChange={(e) => setShowIndividual(e.checked ?? false)} />
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                      Show individual skip controls
                    </div>
                  </label>

                  {/* ── Barcode type selector ── */}
                  <div style={{
                    padding: '12px 14px', borderRadius: 10,
                    background: 'rgba(248,250,252,0.9)', border: '1px solid #e5e7eb',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginBottom: 3 }}>
                      Barcode type
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 500, marginBottom: 10 }}>
                      Choose the format generated for each student.
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {(['linear', 'qr'] as BarcodeType[]).map((type) => {
                        const active = barcodeType === type;
                        const isQR   = type === 'qr';
                        return (
                          <button
                            key={type}
                            className="bc-type-btn"
                            onClick={() => setBarcodeType(type)}
                            style={{
                              position: 'relative',
                              display: 'flex', flexDirection: 'column', alignItems: 'center',
                              gap: 10, padding: '14px 10px', borderRadius: 12, cursor: 'pointer',
                              border: `2px solid ${active ? '#3b82f6' : '#e5e7eb'}`,
                              background: active
                                ? 'linear-gradient(135deg, rgba(219,234,254,0.8), rgba(191,219,254,0.6))'
                                : 'white',
                              boxShadow: active ? '0 0 0 3px rgba(59,130,246,0.12)' : 'none',
                            }}
                          >
                            <div style={{
                              width: 80, height: 44, borderRadius: 6,
                              background: active ? 'white' : '#f8fafc',
                              border: `1px solid ${active ? '#bfdbfe' : '#e5e7eb'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              overflow: 'hidden', flexShrink: 0,
                            }}>
                              {isQR ? (
                                <svg viewBox="0 0 44 44" width="36" height="36">
                                  <rect x="2" y="2" width="16" height="16" rx="2" fill={active ? '#1e40af' : '#94a3b8'} />
                                  <rect x="5" y="5" width="10" height="10" rx="1" fill="white" />
                                  <rect x="7" y="7" width="6" height="6" rx="1" fill={active ? '#1e40af' : '#94a3b8'} />
                                  <rect x="26" y="2" width="16" height="16" rx="2" fill={active ? '#1e40af' : '#94a3b8'} />
                                  <rect x="29" y="5" width="10" height="10" rx="1" fill="white" />
                                  <rect x="31" y="7" width="6" height="6" rx="1" fill={active ? '#1e40af' : '#94a3b8'} />
                                  <rect x="2" y="26" width="16" height="16" rx="2" fill={active ? '#1e40af' : '#94a3b8'} />
                                  <rect x="5" y="29" width="10" height="10" rx="1" fill="white" />
                                  <rect x="7" y="31" width="6" height="6" rx="1" fill={active ? '#1e40af' : '#94a3b8'} />
                                  {[26,30,34,38].map(x => [26,30,34,38].map(y =>
                                    Math.random() > 0.4 && (
                                      <rect key={`${x}-${y}`} x={x} y={y} width="3" height="3"
                                        fill={active ? '#1e40af' : '#94a3b8'} opacity="0.7" />
                                    )
                                  ))}
                                </svg>
                              ) : (
                                <svg viewBox="0 0 72 36" width="68" height="32">
                                  {[2,4,7,9,11,14,17,19,22,25,27,30,33,35,38,41,44,47,50,52,55,58,60,63,66,68].map((x, i) => (
                                    <rect key={i} x={x} y="4" width={i % 4 === 0 ? 2 : 1} height="28"
                                      fill={active ? '#1e40af' : '#94a3b8'} />
                                  ))}
                                </svg>
                              )}
                            </div>

                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: active ? '#1e40af' : '#374151' }}>
                                {isQR ? 'QR Code' : 'Linear Barcode'}
                              </div>
                              <div style={{ fontSize: 10, color: active ? '#3b82f6' : '#9ca3af', marginTop: 2, fontWeight: 500 }}>
                                {isQR ? 'Scannable with any camera' : 'Code 128 format'}
                              </div>
                            </div>

                            {active && (
                              <div style={{
                                position: 'absolute', top: 8, right: 8,
                                width: 16, height: 16, borderRadius: '50%',
                                background: '#3b82f6', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                              }}>
                                <i className="pi pi-check" style={{ fontSize: 9, color: 'white' }} />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Barcode prefix input */}
                  <div style={{
                    padding: '12px 14px', borderRadius: 10,
                    background: barcodePrefix.trim() ? 'rgba(59,130,246,0.06)' : 'rgba(248,250,252,0.9)',
                    border: `1px solid ${barcodePrefix.trim() ? 'rgba(59,130,246,0.25)' : '#e5e7eb'}`,
                    transition: 'all 0.2s ease',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>
                      Barcode data prefix <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>(optional)</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10, fontWeight: 500 }}>
                      Prepended to the student ID — use as a URL slug or namespace.
                    </div>
                    <div
                      style={{ display: 'flex', alignItems: 'center', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #d1d5db', background: 'white' }}
                      onFocusCapture={(e) => (e.currentTarget.style.borderColor = '#3b82f6')}
                      onBlurCapture={(e)  => (e.currentTarget.style.borderColor = '#d1d5db')}
                    >
                      <div style={{
                        padding: '8px 12px', background: 'linear-gradient(135deg,#f1f5f9,#e2e8f0)',
                        borderRight: '1.5px solid #d1d5db', fontSize: 12, fontWeight: 700,
                        color: '#64748b', whiteSpace: 'nowrap', flexShrink: 0,
                        display: 'flex', alignItems: 'center', gap: 5,
                      }}>
                        <i className="pi pi-barcode" style={{ fontSize: 12 }} />
                        Prefix
                      </div>
                      <input
                        type="text"
                        value={barcodePrefix}
                        onChange={(e) => setBarcodePrefix(e.target.value.replace(/[^A-Za-z0-9\-_/:.]/g, ''))}
                        placeholder="e.g. https://school.com/students/"
                        style={{
                          flex: 1, border: 'none', outline: 'none',
                          padding: '8px 12px', fontSize: 12, fontWeight: 500,
                          color: '#1f2937', background: 'transparent', fontFamily: 'monospace',
                        }}
                      />
                      {barcodePrefix && (
                        <button onClick={() => setBarcodePrefix('')}
                          style={{ padding: '8px 10px', border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}>
                          <i className="pi pi-times" style={{ fontSize: 11 }} />
                        </button>
                      )}
                    </div>
                    <div style={{
                      marginTop: 8, padding: '7px 10px', borderRadius: 7,
                      background: '#f8fafc', border: '1px dashed #cbd5e1',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <i className="pi pi-eye" style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Preview: </span>
                      <code style={{ fontSize: 11, color: '#1e40af', fontWeight: 700, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {barcodePrefix.trim() ? `${barcodePrefix}<student_id>` : '<student_id>'}
                      </code>
                    </div>
                  </div>

                </div>
              </Card>

              {/* ── Individual student list ── */}
              {showIndividual && (
                <Card>
                  <SectionHeader icon="pi pi-users" title="Students" />

                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#f8fafc', borderRadius: 8, padding: '8px 12px',
                    marginBottom: 10, border: '1px solid #e5e7eb', flexWrap: 'wrap', gap: 8,
                  }}>
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                      {students.length} students ·{' '}
                      <span style={{ color: noPictureIds.size > 0 ? '#d97706' : '#9ca3af' }}>
                        {noPictureIds.size} missing photo{noPictureIds.size !== 1 ? 's' : ''}
                      </span>
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {noPictureIds.size > 0 && (
                        <button style={{ fontSize: 12, color: '#1e40af', cursor: 'pointer', background: 'none', border: 'none', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2, padding: 0 }}
                          onClick={skipAllNoPicture}>Skip all without photo</button>
                      )}
                      {manuallySkipped.size > 0 && (
                        <button style={{ fontSize: 12, color: '#ef4444', cursor: 'pointer', background: 'none', border: 'none', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2, padding: 0 }}
                          onClick={clearAllSkips}>Clear all skips</button>
                      )}
                    </div>
                  </div>

                  <div style={{ maxHeight: 220, overflowY: 'auto', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                    {students.map((student) => {
                      const hasNoPic    = noPictureIds.has(student.id);
                      const autoSkipped = skipNoPicture && hasNoPic;
                      const manSkipped  = manuallySkipped.has(student.id);
                      const isSkipped   = autoSkipped || manSkipped;

                      return (
                        <div key={student.id} className="student-row" style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 12px', borderBottom: '1px solid #f3f4f6',
                          opacity: isSkipped ? 0.45 : 1, transition: 'all 0.15s ease',
                        }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                            background: hasNoPic ? 'linear-gradient(135deg,#fef3c7,#fde68a)' : 'linear-gradient(135deg,#dbeafe,#bfdbfe)',
                            color: hasNoPic ? '#92400e' : '#1e40af',
                            border: `1.5px solid ${hasNoPic ? '#fcd34d' : '#93c5fd'}`,
                          }}>
                            {getInitials(student.name)}
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {student.name}
                            </div>
                            <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 2 }}>
                              {hasNoPic && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#fef3c7', color: '#92400e', fontWeight: 600, border: '1px solid #fcd34d' }}>no photo</span>}
                              {isSkipped && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: '#fee2e2', color: '#991b1b', fontWeight: 600, border: '1px solid #fca5a5' }}>{autoSkipped ? 'auto-skipped' : 'skipped'}</span>}
                            </div>
                          </div>

                          {!autoSkipped && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                              <Checkbox inputId={`skip-cb-${student.id}`} checked={manSkipped} onChange={() => toggleManualSkip(student.id)} />
                              <label htmlFor={`skip-cb-${student.id}`} style={{ fontSize: 12, fontWeight: 500, color: manSkipped ? '#991b1b' : '#64748b', cursor: 'pointer', userSelect: 'none' }}>
                                Skip
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Summary pills */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '0 2px' }}>
                <span style={{ fontSize: 12, padding: '5px 13px', borderRadius: 99, background: 'linear-gradient(135deg,#dbeafe,#bfdbfe)', color: '#1e40af', fontWeight: 600, display: 'inline-flex', alignItems: 'center', border: '1px solid rgba(59,130,246,0.2)' }}>
                  <i className="pi pi-check" style={{ fontSize: 10, marginRight: 5 }} />
                  {studentsToExport.length} will export
                </span>
                {skippedCount > 0 && (
                  <span style={{ fontSize: 12, padding: '5px 13px', borderRadius: 99, background: '#fef3c7', color: '#92400e', fontWeight: 600, display: 'inline-flex', alignItems: 'center', border: '1px solid #fcd34d' }}>
                    <i className="pi pi-times" style={{ fontSize: 10, marginRight: 5 }} />
                    {skippedCount} skipped
                  </span>
                )}
                {studentsToExport.length === 0 && (
                  <span style={{ fontSize: 12, padding: '5px 13px', borderRadius: 99, background: '#fee2e2', color: '#991b1b', fontWeight: 600, border: '1px solid #fca5a5' }}>
                    All students skipped — nothing to export
                  </span>
                )}
              </div>
            </>
          )}

          {/* Progress */}
          {(exporting || done) && (
            <Card>
              <SectionHeader icon={done ? 'pi pi-check-circle' : 'pi pi-sync'} title={done ? 'Export Complete' : 'Exporting…'} />
              <div style={{ height: 8, borderRadius: 99, background: '#e5e7eb', overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ height: '100%', borderRadius: 99, width: `${progress}%`, background: done ? 'linear-gradient(90deg, #1e3a8a, #3b82f6)' : 'linear-gradient(90deg, #3b82f6, #60a5fa)', transition: 'width 0.3s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{progressLabel}</span>
                <span style={{ fontSize: 12, color: '#1e40af', fontWeight: 700 }}>{progress}%</span>
              </div>
              {done && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'linear-gradient(135deg,#dbeafe,#bfdbfe)', borderRadius: 10, padding: '12px 16px', border: '1px solid rgba(59,130,246,0.25)' }}>
                  <i className="pi pi-check-circle" style={{ fontSize: 18, color: '#1e40af' }} />
                  <span style={{ fontSize: 13, color: '#1e3a8a', fontWeight: 600 }}>Download started! Check your downloads folder.</span>
                </div>
              )}
            </Card>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 4 }}>
            <button className="ez-cancel-btn" style={{ padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: 'white', fontSize: 13, fontWeight: 600, color: '#6b7280', cursor: exporting ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease' }}
              onClick={handleClose} disabled={exporting}>
              {done ? 'Close' : 'Cancel'}
            </button>
            {!done && (
              <button className="ez-export-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 22px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', fontSize: 13, fontWeight: 700, color: 'white', boxShadow: '0 4px 14px rgba(59,130,246,0.35)', cursor: exporting || studentsToExport.length === 0 ? 'not-allowed' : 'pointer', opacity: exporting || studentsToExport.length === 0 ? 0.6 : 1, transition: 'all 0.2s ease' }}
                onClick={runExport} disabled={exporting || studentsToExport.length === 0}>
                {exporting
                  ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 13 }} /> Exporting…</>
                  : <><i className="pi pi-download" style={{ fontSize: 13 }} /> Export {studentsToExport.length} student{studentsToExport.length !== 1 ? 's' : ''}</>
                }
              </button>
            )}
          </div>

        </div>
      </Dialog>
    </>
  );
};

export default ExportStudentsZip;